import { Player } from './player';
import { getJingleUrl, getPlaylist, getSong, getSongUrl } from '../slices/api/catalog';
import { EventType, type Queue, type QueueItem, queueItem, requestItem, playItem, downloadItem, stopItem, movePlayIndex, stop, DefaultChannelVolume, checkedForJingles, setJingleId } from '../slices/local/orchestrator';
import { convertBlobToBase64 } from '../helpers/convert-blob-to-base64';
import { Jingle } from '../slices/local/user';
import { getSentry } from '../helpers/get-sentry';
import { AnyAction } from 'redux';
import { ActionHandler } from 'middlewares/orchestrator';

export abstract class LocalPlayer extends Player {
  public override getSchedulerInterval(): number {
    return 500;
  }

  protected getJingleCheckInterval(): number {
    // Do not use smaller values then 60000 to avoid duplicate jingle playbacks!
    return 60000;
  }

  protected getFadeTime(): number {
    return 5000;
  }

  protected getRelativeFadeTime(a: number, b: number): number {
    const dist = Math.abs(b - a);
    return dist * this.getFadeTime();
  }

  protected normalizeVolume(vol: number): number {
    return vol / DefaultChannelVolume;
  }

  protected getPlaylistThreshold(): number {
    return 3;
  }

  protected getPrefetchCount(): number {
    return 3;
  }

  protected getQueue(): Queue | undefined {
    if (this.state === undefined) {
      return;
    }
    return this.state.queue;
  }

  protected getQueueItem(index: number): QueueItem | undefined {
    const queue = this.getQueue();
    return queue?.entries[index];
  }

  public override changeStation(prevStationId: number): ActionHandler | undefined {
    if (this.state === undefined) {
      return;
    }

    const queue = this.state.queue;
    const playlist = queue.entries.slice(queue.playIndex);

    // Stop playing item
    const index = playlist.findIndex(e => e.playing);
    if (index >= 0) {
      if (index > 0) {
        console.warn('Oops, play index is not equal to the first song in the playlist');
      }

      this.actionQueue.push(async dispatch => {
        dispatch(stopItem({
          ...this.selector,
          index: queue.playIndex + index
        }));
      });
    }

    // Move play index to end to force scheduler to load a new playlist
    this.actionQueue.push(async dispatch => {
      dispatch(movePlayIndex({
        ...this.selector,
        index: queue.entries.length
      }));
    });

    //
    // Next call to schedule will load playlist for the
    // following station and perform the change this way.
    //

    // Stop scheduler if station has been stopped
    if (this.state?.stationId === undefined) {
      this.actionQueue.push(async dispatch => {
        dispatch(stop({
          ...this.selector
        }));
      });
    }
  }

  public override schedule(): void {
    if (this.state === undefined) {
      return;
    }

    const queue = this.state.queue;
    const playlist = queue.entries.slice(queue.playIndex);

    //
    // Load playlist entries
    //
    if (playlist.length <= this.getPlaylistThreshold()) {
      this.actionQueue.push(async dispatch => {
        if (this.state?.stationId === undefined) {
          return;
        }

        getSentry().startSpanManual({
          name: 'fetch-playlist',
          attributes: {
            ...this.selector,
            stationId: this.state.stationId
          }
        }, async span => {
          if (this.state?.stationId === undefined) {
            return;
          }

          const lastPlayedSongId = this.state.queue.entries.length > 0
            ? this.state.queue.entries[this.state.queue.entries.length - 1].songId
            : undefined;
          const result = await dispatch(getPlaylist.initiate({
            stationId: this.state.stationId,
            lastPlayedSongId: lastPlayedSongId,
            time: new Date().toLocaleString('de-DE').replace(',', '').replace(/\./g, '-').replace(/^(\d{2})-(\d{2})-(\d{4})/, '$3-$2-$1')
          }));

          if (result.data?.ok === true) {
            result.data?.playlist.forEach((e: any) => {
              dispatch(queueItem({
                ...this.selector,
                playing: false,
                songId: parseInt(e.song_id),
                persistentId: e.persistent_id,
                title: e.name,
                artist: e.artist,
                genre: e.genre,
                duration: e.total_time / 1000.0,
                atIndex: -1
              }));
            });
          }

          span.setStatus(result.data?.sentrySpanStatus);
          span.end();
        });
      });
    }

    if (playlist.length > 0) {
      //
      // Check if there's a song playing. If not, take the first
      // which has been downloaded.
      //
      if (!playlist.some(i => i.playing)) {
        const nextIndex = playlist.findIndex(i => !i.playing && !i.events.some(e => e.type >= EventType.Error) && i.events.some(e => e.type === EventType.Downloaded));
        if (nextIndex >= 0) {
          const entry = playlist[nextIndex];
          this.actionQueue.push(async dispatch => {
            dispatch(playItem({
              ...this.selector,
              index: queue.playIndex + nextIndex,
              ...(entry.playFromPosition ? { fromPosition: entry.playFromPosition } : {})
            }));
          });
        }
      }

      //
      // We prefetch as much as possible, until we reach the prefetch
      // count, in order to have them ready for playback.
      //
      for (let i = 0, prefetching = 0;
        i < playlist.length && prefetching < this.getPrefetchCount();
        i++) {
        const entry = playlist[i];

        // Skip failed and downloaded items
        if (entry.events.some(e => e.type >= EventType.Error) || entry.events.some(e => e.type === EventType.Downloaded)) {
          continue;
        }

        prefetching++;

        // Skip those which failed and those we currently prefetch, too
        if (entry.events.some(e => e.type >= EventType.Error) || entry.events.some(e => e.type === EventType.Requested)) {
          continue;
        }

        // Request song
        this.actionQueue.push(async dispatch => {
          if (this.state?.stationId !== undefined && entry.jingleId === undefined) {
            const songUrlArgs = {
              persistentId: entry.persistentId,
              stationId: this.state?.stationId
            };

            // First check if song exists
            const result = await dispatch(getSong.initiate(songUrlArgs));
            if (result.isSuccess) {
              // If yes, request
              dispatch(requestItem({
                ...this.selector,
                index: queue.playIndex + i,
                uri: getSongUrl({
                  ...songUrlArgs,
                  absolute: true
                })
              }));
            } else {
              getSentry().captureMessage('Download item error', {
                tags: {
                  ...this.selector,
                  ...songUrlArgs
                }
              });
            }
          } else if (entry.jingleId !== undefined) {
            dispatch(requestItem({
              ...this.selector,
              index: queue.playIndex + i,
              uri: getJingleUrl({
                jingleId: entry.jingleId
              })
            }));
          } else {
            console.warn('Could not request song', entry);
          }
        });
      }
    }

    // Check for recurring jingles
    if (this.userState !== undefined) {
      const date = new Date();
      const time = +date;

      if (time >= this.state.lastJingleCheckTime + Math.max(60000, this.getJingleCheckInterval())) {
        this.actionQueue.push(async dispatch => {
          dispatch(checkedForJingles({
            ...this.selector
          }));
        });

        this.userState.jingles
          .filter(j => j.playAt.length > 0)
          .filter(j => j.playAt.some(t => t.split(':').map(c => +c).join(':') === `${date.getHours()}:${date.getMinutes()}`))
          .slice(0, 1)
          .forEach(j => {
            // Only play jingle if the station is playing
            if (this.state?.jingleId === undefined && playlist.some(e => e.playing)) {
              this.actionQueue.push(async dispatch => {
                dispatch(setJingleId({
                  ...this.selector,
                  jingleId: j.jingleId
                }));
              });
            }
          });
      }
    }
  }

  protected cacheGet(key: string): string | null {
    return null;
  }

  protected cacheSet(key: string, data: string): boolean {
    return false;
  }

  public override request(index: number): void {
    const queue = this.getQueue();
    const item = this.getQueueItem(index);

    if (item?.sourceUri === undefined) {
      return;
    }

    if (index === queue?.playIndex) {
      //
      // We need the item as fast as possible, so stream directly
      //
      this.actionQueue.push(async dispatch => {
        dispatch(downloadItem({
          ...this.selector,
          index,
          uri: item.sourceUri!
        }));
      });
    } else {
      //
      // Otherwise download the item into a blob
      //
      fetch(item.sourceUri, {
        credentials: 'include'
      })
        .then(async response => await response.blob())
        .then(async blob => {
          const objectUrl = URL.createObjectURL(blob);

          this.actionQueue.push(async dispatch => {
            dispatch(downloadItem({
              ...this.selector,
              index,
              uri: objectUrl
            }));
          });

          this.cacheSet(`item-data-${item.persistentId}`,
            await convertBlobToBase64(blob) as string);

          return blob;
        })
        .catch(async reason => {
          console.error('Error downloading ' + item.sourceUri, reason);

          getSentry().captureMessage('Prebuffer item error', {
            tags: {
              ...this.selector,
              url: item.sourceUri
            }
          });
        });
    }
  }

  public override playJingle(jingle: Jingle | undefined, prevJingleId: number): ActionHandler | undefined {
    if (this.state === undefined || jingle === undefined) {
      return undefined;
    }

    getSentry().startSpan({
      name: 'play-jingle',
      attributes: {
        ...this.selector,
        ...jingle
      }
    }, async () => {
      if (this.state === undefined || jingle === undefined) {
        return;
      }

      const queue = this.state.queue;

      this.actionQueue.push(async dispatch => {
        const atIndex = queue?.playIndex !== undefined ? (queue?.playIndex + 1) : 0;

        // Insert jingle into playback queue
        dispatch(queueItem({
          ...this.selector,
          playing: false,
          songId: jingle.songId,
          persistentId: jingle.persistentId,
          title: jingle.name,
          artist: jingle.artist,
          genre: jingle.genre,
          duration: jingle.duration,
          atIndex,
          jingleId: jingle.jingleId
        }));

        // After that enqueue the currently playing item
        if (queue?.playIndex !== undefined) {
          const playingItem = queue.entries[queue.playIndex];

          if (playingItem !== undefined) {
            const { seconds } = this.progress(queue.playIndex);

            //
            // Taking some safety measures here...
            //
            // Would also sound bad to play the rest of the currently playing item
            // for just a few seconds!
            //
            if (seconds < (playingItem.duration - (this.getFadeTime() / 1000) * 2)) {
              dispatch(queueItem({
                ...this.selector,
                ...playingItem,
                playing: false,
                atIndex: atIndex + 1,
                playFromPosition: seconds
              }));
            }
          }
        }
      });
    });

    return undefined;
  }

  public override stopJingle(prevJingleId: number): ActionHandler | undefined {
    if (this.state === undefined) {
      return undefined;
    }

    const queue = this.state.queue;
    const playlist = queue.entries.slice(queue.playIndex);

    // Stop playing jingle
    const index = playlist.findIndex(e => e.playing && e.jingleId === prevJingleId);
    if (index >= 0) {
      if (index > 0) {
        console.warn('Oops, play index is not equal to the first song in the playlist');
      }

      this.actionQueue.push(async dispatch => {
        dispatch(stopItem({
          ...this.selector,
          index: queue.playIndex + index
        }));
      });
    }

    return undefined;
  }

  public override downloaded(downloadedIndex: number): void {
    if (this.state === undefined) {
      return;
    }

    const item = this.getQueueItem(downloadedIndex);

    // Special jingle handling we start when downloaded
    if (item?.jingleId !== undefined && !item?.playing) {
      const queue = this.state.queue;
      const playlist = queue.entries.slice(queue.playIndex);

      // Stop playing item to trigger jingle
      const index = playlist.findIndex(e => e.playing);
      if (index >= 0) {
        if (index > 0) {
          console.warn('Oops, play index is not equal to the first song in the playlist');
        }

        this.actionQueue.push(async dispatch => {
          dispatch(stopItem({
            ...this.selector,
            index: queue.playIndex + index
          }));
        });
      }
    }
  }

  public override unload(index: number): void {
    const item = this.getQueueItem(index);
    if (item?.destUri !== undefined && item?.destUri?.startsWith('blob:')) {
      URL.revokeObjectURL(item.destUri);
    }
  }

  public override skip(): void {
    if (this.state === undefined) {
      return;
    }

    const queue = this.state.queue;
    const playlist = queue.entries.slice(queue.playIndex);

    // Stop playing item
    const index = playlist.findIndex(e => e.playing);
    if (index >= 0) {
      if (index > 0) {
        console.warn('Oops, play index is not equal to the first song in the playlist');
      }

      this.actionQueue.push(async dispatch => {
        dispatch(stopItem({
          ...this.selector,
          index: queue.playIndex + index
        }));
      });
    }
  }
}
